import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseButton from './Common/CloseButton';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';

const primaryColor = '#5757b0';

const creatorTypeOptions = [
  { value: 'connector', translationKey: 'connector_creator.type_options.connector' },
  { value: 'action', translationKey: 'connector_creator.type_options.action' },
];

const getGeneratedPluginFolderName = (pluginInfo) => {
  const explicitFolder = [
    pluginInfo?.folderName,
    pluginInfo?.folder_name,
    pluginInfo?.plugin?.folderName,
  ].find((value) => String(value || '').trim());
  if (explicitFolder) {
    return String(explicitFolder).trim();
  }

  const pathValue = String(pluginInfo?.folder || pluginInfo?.plugin_json || '').trim();
  if (!pathValue) {
    return '';
  }

  const parts = pathValue.split(/[\\/]/).filter(Boolean);
  if (!parts.length) {
    return '';
  }
  const lastPart = parts[parts.length - 1];
  if (/^(plugin|meta)\.(json|py)$/i.test(lastPart) && parts.length > 1) {
    return parts[parts.length - 2];
  }
  return lastPart;
};

const getCreatorCopy = (creatorType, t) => {
  if (creatorType === 'action') {
    return {
      title: t('connector_creator.action_title', 'Action Creator'),
      description: t(
        'connector_creator.action_description',
        'This beta dialog creates action plugins for workflow post-processing. Describe the action you want to run from the action list.'
      ),
    };
  }

  return {
    title: t('connector_creator.title', 'Connector Creator'),
    description: t('connector_creator.description'),
  };
};

const ConnectorCreatorDialog = ({
  open,
  onClose,
  onPluginCreated,
  defaultCreatorType = 'connector',
  lockCreatorType = false,
  deferCloseAfterCreate = false
}) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages } = useWebSocket();
  const [request, setRequest] = useState('');
  const [creatorType, setCreatorType] = useState(defaultCreatorType);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [createdPluginInfo, setCreatedPluginInfo] = useState(null);
  const creatorCopy = getCreatorCopy(creatorType, t);

  useEffect(() => {
    setCreatorType(defaultCreatorType);
    if (!open) {
      setRequest('');
      setIsGenerating(false);
      setIsCreating(false);
      setIsRepairing(false);
      setErrorMessage('');
      setResultMessage('');
      setGeneratedPlan(null);
      setCreatedPluginInfo(null);
    }
  }, [defaultCreatorType, open]);

  useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }
    const unsubscribe = subscribeToMessages(['generatePluginCreator', 'repairGeneratedPlugin'], (message) => {
      const payload = message?.data || {};
      if (message?.command === 'repairGeneratedPlugin') {
        setIsRepairing(false);
        if (payload.status === 'success') {
          setErrorMessage('');
          setResultMessage(payload.message || t('connector_creator.repair_success', 'The generated plugin was repaired and revalidated.'));
          const repairedPlugin = payload.plugin || {};
          setCreatedPluginInfo((current) => ({
            ...(current || {}),
            folderName: payload.folderName || repairedPlugin.folderName || current?.folderName,
          }));
        } else {
          setErrorMessage(payload.message || t('connector_creator.repair_error', 'Unable to repair the generated plugin automatically.'));
          setResultMessage('');
        }
        return;
      }

      if (payload.status === 'processing') {
        setIsGenerating(false);
        setIsCreating(true);
        setIsRepairing(false);
        setErrorMessage('');
        setResultMessage(payload.message || t('connector_creator.creating_feedback'));
        return;
      }
      setIsGenerating(false);
      setIsCreating(false);
      setIsRepairing(false);
      if (payload.status === 'error') {
        setErrorMessage(payload.message || t('connector_creator.generate_error'));
        setResultMessage('');
        return;
      }
      if (payload.status === 'planned') {
        setGeneratedPlan(payload.plan || null);
        setErrorMessage('');
        setResultMessage(payload.message || t('connector_creator.plan_ready'));
        setCreatedPluginInfo(null);
        return;
      }
      if (payload.status === 'created') {
        setGeneratedPlan(payload.plan || null);
        setErrorMessage('');
        setResultMessage(payload.message || t('connector_creator.create_success'));
        setIsCreating(false);
        setCreatedPluginInfo(payload.created || null);
        if (typeof onPluginCreated === 'function') {
          onPluginCreated(payload);
        }
        if (!deferCloseAfterCreate && typeof onClose === 'function') {
          onClose();
        }
        return;
      }
      setErrorMessage('');
      setResultMessage(payload.message || t('connector_creator.generate_success'));
    });
    return unsubscribe;
  }, [deferCloseAfterCreate, onClose, onPluginCreated, subscribeToMessages, t]);

  const handleGenerate = () => {
    const trimmedRequest = String(request || '').trim();
    if (!trimmedRequest || typeof sendMessage !== 'function') {
      return;
    }
    setIsGenerating(true);
    setIsRepairing(false);
    setErrorMessage('');
    setResultMessage('');
    setGeneratedPlan(null);
    sendMessage({
      command: 'generatePluginCreator',
      data: {
        mode: 'plan',
        input: trimmedRequest,
        creatorType,
      }
    });
  };

  const handleAcceptPlan = (isRepairRetry = false) => {
    const trimmedRequest = String(request || '').trim();
    if (!trimmedRequest || !generatedPlan || typeof sendMessage !== 'function') {
      return;
    }
    setIsCreating(true);
    setIsRepairing(false);
    setErrorMessage('');
    setResultMessage(t('connector_creator.creating_feedback'));
    setCreatedPluginInfo(null);
    const repairContext = isRepairRetry && errorMessage
      ? `\n\nPrevious generation error to repair:\n${String(errorMessage).trim()}`
      : '';
    sendMessage({
      command: 'generatePluginCreator',
      data: {
        mode: 'create',
        input: `${trimmedRequest}${repairContext}`,
        creatorType,
        plan: generatedPlan,
      }
    });
  };

  const handleAutomaticFix = () => {
    const folderName = getGeneratedPluginFolderName(createdPluginInfo);
    if (!folderName) {
      handleAcceptPlan(true);
      return;
    }

    if (typeof sendMessage !== 'function') {
      return;
    }

    const pluginType = String(
      createdPluginInfo?.manifest?.type
      || createdPluginInfo?.plugin?.type
      || generatedPlan?.plugin_type
      || ''
    ).trim();
    setIsRepairing(true);
    setIsCreating(false);
    setErrorMessage('');
    setResultMessage(t(
      'connector_creator.repairing_feedback',
      'Reviewing and repairing the generated plugin. This can take a few minutes.'
    ));
    sendMessage({
      command: 'repairGeneratedPlugin',
      data: {
        folderName,
        pluginType,
        type: pluginType,
        error: String(errorMessage || '').trim(),
        message: String(errorMessage || '').trim(),
      },
    });
  };

  const handleModifyPlan = () => {
    setGeneratedPlan(null);
    setCreatedPluginInfo(null);
    setErrorMessage('');
    setResultMessage('');
    setIsGenerating(false);
    setIsCreating(false);
    setIsRepairing(false);
  };

  const handleDialogClose = (event, reason) => {
    if (isCreating && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    if (typeof onClose === 'function') {
      onClose(event, reason);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={isCreating}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '18px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f9f9ff 100%)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AutoAwesomeIcon sx={{ color: primaryColor }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
                {creatorCopy.title}
              </Typography>
              <Chip
                size="small"
                color="warning"
                label={t('workflow.add_action_menu.labels.beta', 'BETA')}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  borderRadius: '999px',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {t('connector_creator.subtitle')}
            </Typography>
          </Box>
        </Box>
        <CloseButton onClick={onClose} sx={{ color: 'text.secondary' }} />
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: '14px',
            backgroundColor: 'rgba(87, 87, 176, 0.08)',
            border: '1px solid rgba(87, 87, 176, 0.18)',
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {creatorCopy.description}
          </Typography>
        </Box>

        {!generatedPlan && (
          <>
            {!lockCreatorType && (
              <TextField
                select
                fullWidth
                margin="normal"
                label={t('connector_creator.type_label')}
                value={creatorType}
                onChange={(event) => setCreatorType(event.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                  '& .MuiInputLabel-outlined.Mui-focused': {
                    color: primaryColor,
                  },
                }}
              >
                {creatorTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.translationKey)}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              fullWidth
              multiline
              minRows={4}
              margin="normal"
              label={t('connector_creator.request_label')}
              placeholder={t('connector_creator.request_placeholder')}
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
                '& .MuiInputLabel-outlined.Mui-focused': {
                  color: primaryColor,
                },
              }}
            />
          </>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: '12px' }}>
            {errorMessage}
          </Alert>
        )}

        {resultMessage && !isCreating && (
          <Alert severity="success" sx={{ mt: 2, borderRadius: '12px' }}>
            {resultMessage}
          </Alert>
        )}

        {isCreating && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: '12px' }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={18} thickness={5} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('connector_creator.creating_feedback')}
                </Typography>
                <LinearProgress />
              </Box>
            </Stack>
          </Alert>
        )}

        {generatedPlan && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: '14px',
              border: '1px solid rgba(87, 87, 176, 0.18)',
              backgroundColor: 'rgba(87, 87, 176, 0.04)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: primaryColor, mb: 1 }}>
              {t('connector_creator.plan_title')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
              {generatedPlan.user_goal || generatedPlan.plan_title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {generatedPlan.summary}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{t('connector_creator.technical_plan_label')}</strong> {generatedPlan.plan_title}
            </Typography>
            {Array.isArray(generatedPlan.user_steps) && generatedPlan.user_steps.length > 0 && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {t('connector_creator.steps_label')}
                </Typography>
                {generatedPlan.user_steps.map((step, index) => (
                  <Typography key={`${step}-${index}`} variant="body2" color="text.secondary">
                    {index + 1}. {step}
                  </Typography>
                ))}
              </>
            )}
            <Typography variant="body2" sx={{ mb: 2 }}><strong>{t('connector_creator.plugin_name_label')}</strong> {generatedPlan.plugin_name}</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              {t('connector_creator.fields_label')}
            </Typography>
            {Array.isArray(generatedPlan.settings_fields) && generatedPlan.settings_fields.map((field) => (
              <Typography key={field.key} variant="body2" color="text.secondary">
                {field.label} ({field.type}) - {field.key}
              </Typography>
            ))}
          </Paper>
        )}

        {createdPluginInfo && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: '14px',
              border: '1px solid rgba(56, 142, 60, 0.25)',
              backgroundColor: 'rgba(76, 175, 80, 0.06)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t('connector_creator.created_title')}
            </Typography>
            <Typography variant="body2">
              <strong>plugin.json</strong> {createdPluginInfo.plugin_json}
            </Typography>
            <Typography variant="body2">
              <strong>plugin.py</strong> {createdPluginInfo.plugin_py}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={isCreating} sx={{ textTransform: 'none' }}>
          {t('common.close', 'Close')}
        </Button>
        {!generatedPlan && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating || !String(request || '').trim()}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
              background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
            }}
          >
            {isGenerating
              ? t('connector_creator.generating')
              : t('connector_creator.generate_button')}
          </Button>
        )}
        {(createdPluginInfo || (generatedPlan && errorMessage)) && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleAutomaticFix}
            disabled={isCreating || isRepairing}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {isRepairing
              ? t('connector_creator.repairing', 'Fixing...')
              : t('connector_creator.repair_button', 'Fix automatically')}
          </Button>
        )}
        {generatedPlan && !createdPluginInfo && (
          <Button
            variant="outlined"
            onClick={handleModifyPlan}
            disabled={isCreating}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {t('connector_creator.modify_plan_button')}
          </Button>
        )}
        {generatedPlan && (
          <Button
            variant="contained"
            color="success"
            onClick={handleAcceptPlan}
            disabled={isCreating || isRepairing}
            sx={{
              textTransform: 'none',
              borderRadius: '999px',
              px: 3,
            }}
          >
            {isCreating
              ? t('connector_creator.creating')
              : t('connector_creator.accept_button')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConnectorCreatorDialog;
